import { useCoinMetricsApiKey, useGetAssetMetricsReferenceQuery } from '@src/providers/CoinMetricsApiProvider';
import { useMemo } from 'react';

interface UseMetricDisplayNameMapResult {
  /** CoinMetrics metric id -> resolved display name. */
  displayNameByMetricId: ReadonlyMap<string, string>;
  isLoading: boolean;
  isError: boolean;
}

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();
const REFERENCE_METRICS_PAGE_SIZE = 10_000;

/**
 * Fetches CoinMetrics asset-metrics reference data and builds a lookup from metric id to
 * display name: `display_name` when present, else `full_name`. Entries with neither are
 * omitted — callers fall back via resolveMetricDisplayName below.
 */
export function useMetricDisplayNameMap(): UseMetricDisplayNameMapResult {
  const apiKey = useCoinMetricsApiKey();

  const { data, isLoading, isUninitialized, isError } = useGetAssetMetricsReferenceQuery(
    { page_size: REFERENCE_METRICS_PAGE_SIZE },
    { skip: !apiKey }
  );

  const displayNameByMetricId = useMemo(() => {
    if (!data) {
      return EMPTY_MAP;
    }
    const map = new Map<string, string>();
    for (const entry of data.data) {
      const name = entry.display_name || entry.full_name;
      if (name) {
        map.set(entry.metric, name);
      }
    }
    return map;
  }, [data]);

  // isUninitialized covers the skip: !apiKey state — key not yet synced into Redux.
  return { displayNameByMetricId, isLoading: isLoading || isUninitialized, isError };
}

/**
 * Resolves a CoinMetrics metric id to a display name: the resolved name when available (from
 * useMetricDisplayNameMap), else the metric id itself — metric ids don't title-case sensibly
 * (e.g. `CapMrktEstUSD`), so there's no cosmetic fallback beyond the raw id.
 */
export function resolveMetricDisplayName(
  metricId: string,
  displayNameByMetricId?: ReadonlyMap<string, string>
): string {
  return displayNameByMetricId?.get(metricId) ?? metricId;
}
